<style lang="scss" scoped>
@import "/src/styles/scss/_variables.scss";
@import "./dropdown.scss";
</style>

<template>
  <div
    :class="`slk-g-dropdown slk-dropdown-v-${validationError ? 'error' : dropdownVariant} ${dropdownVariant == 'disabled' ? 'disabled' : ''} ${
      dropdownVariant == 'display' ? 'display' : ''
    }`"
    :style="`width: ${inputWidth}`"
    ref="dropdown"
    @keydown.down="onMoveDown"
    @keydown.up="onMoveUp"
    @keydown.enter="selectItem"
    @keydown.esc="isDropdownListVisible = false"
  >
    <div class="input-wrap" :class="{ 'disabled-view': disabledView }">
      <div class="input-icon" v-if="hasIcon">
        <svg class="feather">
          <use :href="`/imgs/storybook/feather-sprite.svg#${icon}`" />
        </svg>
      </div>
      <div class="field-wrap">
        <select
          ref="selectRef"
          @focus="handleFocus"
          @blur="handleBlur"
          v-model="selectedItem"
          :class="selectedItem ? 'move' : ''"
          :disabled="dropdownVariant == 'display' || dropdownVariant == 'disabled'"
        >
          <option value="" disabled selected>{{ inputLabel }}</option>
          <option :value="selectedItem">{{ selectedItem }}</option>
        </select>
        <div
          class="selection-display"
          :class="{
            'move': selectedItem,
            'chip-display': displaySelectedAsChip
          }"
          @click="() => {
            isDropdownListVisible = true;
            bottomSheetVisible = true;
          }"
          v-if="!isDropdownListVisible || (selectedItem && !isDropdownListVisible) || !searchFilterEnabled"
        >
          {{ selectedItem ? selectedItem : inputLabel }}
          <v-icon v-if="displaySelectedAsChip" @click="removeClicked" class="remove-selected-icon" size="16">close</v-icon>
        </div>
        <input
          ref="searchFilterInput"
          :class="`search-filter ${selectedItem ? 'move' : ''}`"
          v-if="isDropdownListVisible && searchFilterEnabled"
          v-model="searchTerm"
          @input="filterOptions"
          placeholder="Search..."
        />
        <List
          ref="list"
          :listItems="filteredDropdownOptions"
          class="dropdown-options"
          @item-clicked="handleItemClickInParent"
          v-if="isDropdownListVisible && isScreenSizeAboveBreakpoint"
        />
        <BottomSheet
          :isVisible="bottomSheetVisible"
          @close="handleBottomSheetClose"
          attached
          noHeader
          :drawerHeight="drawerHeight"
          v-if="!isScreenSizeAboveBreakpoint"
        >
          <template #content>
            <List
              ref="list"
              :listItems="filteredDropdownOptions"
              class="dropdown-options"
              @item-clicked="handleItemClickInParent"
            />
          </template>
        </BottomSheet>
        <div class="input-label" v-if="selectedItem && !displaySelectedAsChip">{{ inputLabel }}</div>
      </div>
      <div class="suffix" v-if="hasSuffix && dropdownVariant != 'display'">
        <svg class="icon feather">
          <use href="/imgs/storybook/feather-sprite.svg#chevron-down" />
        </svg>
      </div>
    </div>
    <div class="helper" v-if="isHelperVisible && dropdownVariant != 'display'">{{ helperMessage }}</div>
    <div class="helper" v-if="isErrorVisible && !!validationError">
      {{ validationError }}
    </div>
  </div>
</template>

<script>
import List from "../list/listView.vue";
import BottomSheet from "../bottom-sheet/bottomSheetView.vue";

export default {
  name: "dropdown-view",
  props: {
    searchFilterEnabled: { type: Boolean, default: true },
    selectFirstByDefault: { type: Boolean, default: false },

    variant: { type: String, default: "default" },
    inputLabel: { type: String },
    hasIcon: { type: Boolean },
    icon: { type: String },
    inputWidth: { type: String },
    isHelperVisible: { type: Boolean },
    isErrorVisible: { type: Boolean },
    helperMessage: { type: String },
    hasSuffix: { type: Boolean },
    suffixLabel: { type: String },
    dropdownOptions: { type: Array },
    itemSelected: { type: String },
    drawerHeight: { type: String, default: "auto" },
    displaySelectedAsChip: { type: Boolean, default: false },
    disabledView: { type: Boolean, default: false },
    rules: { type: Array },
  },
  components: { List, BottomSheet },
  data() {
    return {
      selectedItem: this.itemSelected,
      isDropdownListVisible: false,
      dropdownVariant: this.variant,
      searchTerm: "",
      filteredOptions: [],
      bottomSheetVisible: false,
      isScreenSizeAboveBreakpoint: window.innerWidth > 768, // Adjust breakpoint as needed
      validationError: ""
    };
  },
  mounted() {
    window.addEventListener("mousedown", this.handleBodyClick);
    this.$refs.dropdown.addEventListener("click", this.handleDropdownClick);

    // Update isScreenSizeAboveBreakpoint on window resize
    window.addEventListener("resize", this.updateScreenSize);

    // Select the first option if the prop is set to true
    if (this.selectFirstByDefault && this.dropdownOptions.length > 0) {
      this.selectedItem = this.dropdownOptions[0].label;
      this.$emit("item-selected", this.selectedItem);
    }
  },
  beforeDestroy() {
    window.removeEventListener("mousedown", this.handleBodyClick);
    this.$refs.dropdown.removeEventListener("click", this.handleDropdownClick);

    window.removeEventListener("resize", this.updateScreenSize);
  },
  computed: {
    filteredDropdownOptions() {
      // Only filter options if the search filter is enabled
      if (this.searchFilterEnabled) {
        return this.dropdownOptions.filter((option) =>
          option.label.toLowerCase().includes(this.searchTerm.toLowerCase())
        );
      } else {
        return this.dropdownOptions;
      }
    },
  },
  methods: {
    handleBottomSheetClose() {
      this.bottomSheetVisible = false;
    },
    updateScreenSize() {
      this.isScreenSizeAboveBreakpoint = window.innerWidth > 768; // Adjust breakpoint as needed
    },

    //
    filterOptions() {
      this.filteredOptions = this.dropdownOptions.filter((option) =>
        option.label.toLowerCase().includes(this.searchTerm.toLowerCase())
      );
    },

    handleFocus() {
      this.dropdownVariant = "active";
    },
    handleBlur() {
      this.dropdownVariant = this.variant;
    },
    handleItemClickInParent(clickedItem) {
      console.log("Clicked Item in Parent:", clickedItem.label);
      this.selectedItem = clickedItem.label;
      this.isDropdownListVisible = false;
      this.bottomSheetVisible = false;
      this.validationError = "";

      // Emit the selected value to the parent component
      this.$emit("item-selected", this.selectedItem);
      this.$emit("value-selected", clickedItem);
    },
    handleDropdownClick(event) {
      event.stopPropagation();
    },
    handleBodyClick(event) {
      const dropdownElement = this.$refs.dropdown;

      if (dropdownElement && dropdownElement.contains(event.target)) {
        // Clicked inside the dropdown, do nothing
      } else {
        this.isDropdownListVisible = false;
        this.bottomSheetVisible = false;
      }
    },
    onMoveDown() {
      if (!this.isDropdownListVisible) {
        this.isDropdownListVisible = true;
        this.bottomSheetVisible = true;
      } else {
        this.$refs.list.nextItem();
      }
    },
    onMoveUp() {
      if (this.isDropdownListVisible) {
        this.$refs.list.previousItem();
      }
    },
    selectItem() {
      this.$refs.list.selectItem();
      this.isDropdownListVisible = false;
      this.bottomSheetVisible = false;
    },
    removeClicked(event) {
      event.stopPropagation();
      this.$emit("remove-selected");
      this.isDropdownListVisible = false;
      this.bottomSheetVisible = false;
    },
    validateInput() {
      const inputValue = this.selectedItem;
      for (const rule of this.rules) {
        const result = rule(inputValue);
        if (result !== true) {
          this.validationError = result;
          return;
        }
      }
    }
  },
  watch: {
    variant(newVal, oldVal) {
      // Respond to changes in the 'myProp' prop
      this.dropdownVariant = newVal;
      console.log("myProp changed:", newVal);
    },
    isDropdownListVisible(newValue) {
      if (newValue && this.searchFilterEnabled) {
        this.$nextTick(() => {
          // Check if the element exists before trying to access its focus method
          if (this.$refs.searchFilterInput) {
            this.$refs.searchFilterInput.focus();
          }
        });
      }
      if (!newValue && !this.selectedItem) {
        const inputValue = event.target.value;
        for (const rule of this.rules) {
          const result = rule(inputValue);
          if (result !== true) {
            this.validationError = result;
            return;
          }
        }
      }
    },
    itemSelected(newValue) {
      this.selectedItem = newValue;
    },
  },
};
</script>
